"use strict";

/**
 * SapPostingConfigController
 *
 * Developer-side settings page that allows a developer user to configure
 * the SAP posting-date restriction window. Backed by the API endpoints
 * `GET / POST / PUT sec/developer/sap-posting-config` (developer-self;
 * scoped by auth context). Read access is gated by
 * `DMS_SAP_POSTING_CONFIG_VIEW`; edit/save by `DMS_SAP_POSTING_CONFIG_MANAGE`.
 *
 * WEB-4570: `unrestricted_mode` switches every posting-date restriction off
 * (Financial Period Close still applies server-side). While it is on, the
 * settings below it are read-only but keep their values.
 */
class SapPostingConfigController {

    constructor(SSHttpService, SSUtilService, SSAlertService, SSUserService, OTPService) {
        const self = this;

        self.SSHttpService = SSHttpService;
        self.SSUtilService = SSUtilService;
        self.SSAlertService = SSAlertService;
        self.SSUserService = SSUserService;
        self.OTPService = OTPService;

        self.data = SHOWSUITE.data;

        self.has_access = false;

        self.WORKING_DAYS = [
            {id: 1, label: 'Mon', check: true},
            {id: 2, label: 'Tue', check: true},
            {id: 3, label: 'Wed', check: true},
            {id: 4, label: 'Thu', check: true},
            {id: 5, label: 'Fri', check: true},
            {id: 6, label: 'Sat', check: false},
            {id: 7, label: 'Sun', check: false}
        ];

        self.workingDaysDict = angular.copy(self.WORKING_DAYS);
        self.postingConfig = self._defaultConfig();
        // Snapshot of the values as last loaded / saved — drives "Unsaved changes".
        self.savedSnapshot = self._snapshot();

        if (self.SSUserService.isDeveloper()) {
            self.has_access = true;
            self.init();
        }
    }

    init() {
        const self = this;

        if (!self.SSUserService.hasPermission('DMS_SAP_POSTING_CONFIG_VIEW')) {
            return;
        }
        self.fetchConfig();
    }

    /**
     * Default config values used when no row exists yet for the developer.
     * Matches the API-side defaults so the UI is consistent before save.
     */
    _defaultConfig() {
        return {
            id: null,
            is_enabled: 0,
            unrestricted_mode: 0,
            allowed_days: 5,
            working_days: '1,2,3,4,5',
            observe_public_holidays: 1,
            country_code: 'SG'
        };
    }

    /**
     * Load the developer's current SAP posting config.
     * If no record exists, seed the form with safe defaults.
     */
    fetchConfig() {
        const self = this;

        let url = 'sec/developer/sap-posting-config';

        loading(true);
        self.SSHttpService.getAPIRequest(url).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            let row = null;
            if (response && response.data) {
                row = response.data.sap_posting_config || response.data.config || null;
            }

            if (row && row.id) {
                self.postingConfig = {
                    id: row.id,
                    is_enabled: row.is_enabled ? 1 : 0,
                    // Absent on servers without WEB-4568 → off.
                    unrestricted_mode: row.unrestricted_mode == 1 ? 1 : 0,
                    allowed_days: row.allowed_days || 5,
                    working_days: row.working_days || '1,2,3,4,5',
                    observe_public_holidays: row.observe_public_holidays ? 1 : 0,
                    country_code: row.country_code || 'SG',
                    updated_at: row.updated_at || null
                };
            } else {
                self.postingConfig = self._defaultConfig();
            }

            self._applyWorkingDaysToDict(self.postingConfig.working_days);
            self.savedSnapshot = self._snapshot();
        });
    }

    /**
     * True when the settings below Unrestricted Mode may be edited.
     */
    canEditSettings() {
        const self = this;

        return self.SSUserService.hasPermission('DMS_SAP_POSTING_CONFIG_MANAGE')
            && self.postingConfig.unrestricted_mode != 1;
    }

    /**
     * True when Allowed Working Days, Working Days and Observe Public Holidays may be
     * edited. They only apply while "Restrict to First N Working Days" is on, so they
     * are read-only (values kept) whenever it is off.
     */
    canEditRestrictionFields() {
        const self = this;

        return self.canEditSettings() && self.postingConfig.is_enabled == 1;
    }

    /**
     * ng-change of the Unrestricted Mode toggle. Switching OFF applies at once;
     * switching ON is reverted until the user confirms in the dialog. Nothing is
     * persisted until Save.
     */
    onUnrestrictedModeChange() {
        const self = this;

        if (self.postingConfig.unrestricted_mode != 1) {
            return;
        }
        self.postingConfig.unrestricted_mode = 0;
        $('#unrestricted_mode_confirmation_modal').modal('show');
    }

    confirmUnrestrictedMode() {
        const self = this;

        self.postingConfig.unrestricted_mode = 1;
        $('#unrestricted_mode_confirmation_modal').modal('hide');
    }

    hasUnsavedChanges() {
        const self = this;

        return !angular.equals(self.savedSnapshot, self._snapshot());
    }

    _snapshot() {
        const self = this;

        return {
            is_enabled: self.postingConfig.is_enabled == 1,
            unrestricted_mode: self.postingConfig.unrestricted_mode == 1,
            allowed_days: (self.postingConfig.allowed_days == null ? '' : String(self.postingConfig.allowed_days)).trim(),
            working_days: self._workingDaysCsvFromDict(),
            observe_public_holidays: self.postingConfig.observe_public_holidays == 1
        };
    }

    /**
     * Parse a comma-separated working-days string (ISO 1..7) and toggle
     * the matching checkboxes in workingDaysDict.
     */
    _applyWorkingDaysToDict(workingDaysCsv) {
        const self = this;

        let parts = (workingDaysCsv || '').toString().split(',').map(s => parseInt(s, 10)).filter(n => !isNaN(n));
        self.workingDaysDict = self.WORKING_DAYS.map(d => angular.extend({}, d, {check: parts.indexOf(d.id) !== -1}));
    }

    /**
     * Serialise the checked workingDaysDict back to a sorted CSV string.
     */
    _workingDaysCsvFromDict() {
        const self = this;

        return self.workingDaysDict
            .filter(d => d.check)
            .map(d => d.id)
            .sort((a, b) => a - b)
            .join(',');
    }

    savePostingConfig() {
        const self = this;

        if (!self.SSUserService.hasPermission('DMS_SAP_POSTING_CONFIG_MANAGE')) {
            return;
        }

        let allowed = parseInt(self.postingConfig.allowed_days, 10);
        if (isNaN(allowed) || allowed < 1 || allowed > 31) {
            self.SSAlertService.danger('Validation Error', 'Allowed Working Days must be between 1 and 31.');
            return;
        }

        let workingDaysCsv = self._workingDaysCsvFromDict();
        if (!workingDaysCsv) {
            self.SSAlertService.danger('Validation Error', 'Please select at least one working day.');
            return;
        }

        self.OTPService.getVerified(self.OTPService.TYPE_SESSION_2FA, function () {
            self._save(allowed, workingDaysCsv);
        });
    }

    _save(allowed, workingDaysCsv) {
        const self = this;

        let url = 'sec/developer/sap-posting-config';
        let params = {
            is_enabled: self.postingConfig.is_enabled ? 1 : 0,
            unrestricted_mode: self.postingConfig.unrestricted_mode == 1 ? 1 : 0,
            allowed_days: allowed,
            working_days: workingDaysCsv,
            observe_public_holidays: self.postingConfig.observe_public_holidays ? 1 : 0,
            country_code: 'SG'
        };

        let handleResponse = function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            self.SSAlertService.success('Posting date settings saved successfully.');
            self.fetchConfig();
        };

        loading(true);
        if (self.postingConfig.id) {
            self.SSHttpService.putAPIRequest(url, params).then(handleResponse);
        } else {
            self.SSHttpService.postAPIRequest(url, params).then(handleResponse);
        }
    }

}

SapPostingConfigController.$inject = [
    'SSHttpService',
    'SSUtilService',
    'SSAlertService',
    'SSUserService',
    'OTPService'
];
app.controller('SapPostingConfigController', SapPostingConfigController);
